import { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import _ from "lodash";

import Autocomplete from "components/DesignComponents/Autocomplete";
import AuthContext from "context/auth/authContext";
import { API_IS_BEING_CANCELLED } from "shared/constants/strings";
import { sortAccessGroupOptions } from "shared/functions";
import { useApi } from "shared/hooks/useApi";
import { useIsMounted } from "shared/hooks/useIsMounted";
import { DROPDOWN_SIZES, ENDPOINTS } from "shared/types/enums";
import { AccessGroup } from "shared/types/interfaces";

import { AccessGroupsProps } from "./types";

const AccessGroups = ({
    initialAccessGroups,
    selectedUserAccessGroups,
    initialCompany,
    hasCompanyChanged,
    isSubmitLoading,
    isDisabled,
    onChange
}: AccessGroupsProps) => {
    const { accessGroups } = useContext(AuthContext);
    const { t } = useTranslation();
    const isMounted = useIsMounted();
    const { cancelSource, getData, handleResponse, isCanceled } = useApi();

    const [isLoading, setIsLoading] = useState(true);

    const [companyAccessGroup, setCompanyAccessGroup] =
        useState<AccessGroup | null>(null);

    useEffect(() => {
        if (initialCompany) {
            const getCompanyAccessGroup = async () => {
                try {
                    setIsLoading(true);

                    const {
                        data: { access_group }
                    } = await getData(
                        `${ENDPOINTS.Companies}/${initialCompany.id}`
                    );

                    if (isMounted) {
                        setCompanyAccessGroup(access_group);
                    }
                } catch (error) {
                    if (isMounted && !isCanceled(error)) {
                        setCompanyAccessGroup(null);
                        handleResponse(error);
                    }
                }

                setIsLoading(false);
            };

            getCompanyAccessGroup();
        }

        return () => {
            cancelSource.cancel(API_IS_BEING_CANCELLED);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialCompany]);

    const shouldCompanyAccessGroupBeDisabled = (option: AccessGroup) => {
        if (hasCompanyChanged) {
            return false;
        }

        if (!companyAccessGroup) return true;

        const companyAccessGroupId = companyAccessGroup.id;

        const isCompanyAccessGroupSelected = () => {
            const selectedAccessGroupIds = selectedUserAccessGroups.map(
                group => group.id
            );

            return selectedAccessGroupIds.includes(companyAccessGroupId);
        };

        return (
            companyAccessGroupId === option.id && isCompanyAccessGroupSelected()
        );
    };

    const isOptionDisabled = (option: AccessGroup) =>
        shouldCompanyAccessGroupBeDisabled(option) ||
        !accessGroups.some(group => group.id === option.id);

    const isEveryOptionDisabled = () =>
        selectedUserAccessGroups.every(option => isOptionDisabled(option));

    const mergedUniqueAccessGroups = _.unionBy(
        accessGroups,
        initialAccessGroups,
        "id"
    );

    const sortedAccessGroups = sortAccessGroupOptions(mergedUniqueAccessGroups);

    return (
        <Autocomplete
            size={DROPDOWN_SIZES.Medium}
            multiple
            hasCheckbox
            fullWidth
            disableClearable={isEveryOptionDisabled()}
            readOnly={isSubmitLoading}
            loading={isLoading}
            options={!companyAccessGroup ? [] : sortedAccessGroups}
            value={
                !companyAccessGroup
                    ? []
                    : sortAccessGroupOptions(selectedUserAccessGroups)
            }
            labelLeft={t("Table##access groups")}
            textFieldParams={{
                placeholder:
                    !companyAccessGroup || selectedUserAccessGroups.length === 0
                        ? t("Dialog##assign access group")
                        : ""
            }}
            onChange={(_, val, reason) => {
                if (reason === "clear") {
                    onChange(
                        selectedUserAccessGroups.filter(group =>
                            isOptionDisabled(group)
                        )
                    );

                    return;
                }

                onChange(val);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option: AccessGroup) => option.name}
            getOptionDisabled={(option: AccessGroup) =>
                isOptionDisabled(option)
            }
            disabled={isDisabled}
            customStyle={{ marginBottom: "16px" }}
        />
    );
};

export default AccessGroups;
